import React, {useState} from 'react';
import TimeFormatter from '../../util/TimeFormatter';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType, TscStyles, TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import styled from 'styled-components';
import CommentMarker from '../../static/svg/CommentMarker.svg';
import {IComment} from '../../model/commentModel';
import {AnnotationModes} from '../../constants/Constants';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {selectedConversationStore, updateSelectedConversationState} from '../../context/selectedConversationProvider';
import {commentsStore} from '../../context/commentsProvider';
import {updateAnnotationManagerState} from '../../context/annotationManagerProvider';
import {useTscTheme} from '../../context/themeProvider';
import {quizDataStore} from '../../context/quizDataProvider';
import {importMediaInterface} from '../../hooks/dynamicImport';

const lowerArrowThreshold = 2;
const upperArrowThreshold = 98;

const CommentMarkerPlugin = styled.div`
   top: -1rem;
   width: 100%;
   display: flex;
   position: absolute;
`;

export const CommentMarkerWrapper = styled.div<{isSelected: boolean; leftOffset: number; isHovering: boolean; theme: TscThemeNames}>`
   position: absolute;
   pointer-events: auto;
   left: ${props => props.leftOffset}%;
   transform: translateX(-50%);

   &:after {
      content: '';
      display: ${props => props.isHovering && props.leftOffset > lowerArrowThreshold && props.leftOffset < upperArrowThreshold ? 'block' : 'none'};
      border-width: 10px 8px 0;
      border-style: solid;
      border-color: ${TscStyles.color.ui.dusk.mediumDark} transparent transparent transparent;
      position: absolute;
      left: -.125rem;
      bottom: 1.875rem;
      animation: show-comment-preview 200ms ease;
   }

   svg {
      height: 1rem;
      width: 0.875rem;
      fill: ${props => props.isSelected ? TscStyles.color.action.primary : props.theme === TscThemeName.dusk ? TscStyles.color.text.mediumDark : TscStyles.color.ui.dawn.lightMedium};

      path {
         border: 1px solid white;
      }

      .glyph-outer {
         fill: ${props => props.isSelected && props.theme === TscThemeName.dusk ? TscStyles.color.ui.dawn.lightMedium :
            props.isSelected && props.theme === TscThemeName.dawn ? TscStyles.color.text.mediumDark :
               props.theme === TscThemeName.dusk ? TscStyles.color.text.lightMedium :
                  TscStyles.color.text.mediumDark};
      }
   }
`;

export const CommentPreview = styled.div<{leftOffset: number}>`
   background: ${TscStyles.color.ui.dusk.mediumDark};
   color: ${TscStyles.color.text.light};
   position: absolute;
   left: ${props => props.leftOffset}px;
   bottom: 0.25rem;
   padding: 0.5rem !important;
   border-radius: ${TscStyles.border.radius.lg};
   width: 12.5rem;
`;

export const PreviewText = styled.div`
   overflow: hidden;
   text-overflow: ellipsis;
   display: -webkit-box;
   word-wrap: break-word;
   -webkit-box-orient: vertical;
   -webkit-line-clamp: 3;
   line-height: 1rem; /* fallback */
   max-height: 3rem;
`;

export const PreviewAuthor = styled.div`
   font-weight: 600;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;

export const CommentMarkersBase: React.FC<ICommentMarkersProps & IStateMappedProps> = ({mediaDuration, SelectedComment, comments, IsQuizzing}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [previewState, setPreviewState] = useState({showPreview: false, previewXoffset: -1, text: '', authorName: '', comment: null});

   const calculatePreviewX = (target: Element) => {
      const commentPreviewWidth = 200;
      const previewHalfWidth = commentPreviewWidth / 2;
      const markerWidth = 14;
      const halfMarkerWidth = markerWidth / 2;
      const edgeOffset = 0;
      const targetRect = target.getBoundingClientRect();
      const playerRect = document.querySelector('.tsc-smartplayer').getBoundingClientRect();
      const offsetX = targetRect.left + halfMarkerWidth - playerRect.left;
      const centeredOffsetX = offsetX - previewHalfWidth > 0 ? offsetX - previewHalfWidth : -edgeOffset;
      const maxX = playerRect.width - (commentPreviewWidth - edgeOffset);

      return centeredOffsetX < maxX ? centeredOffsetX : maxX;
   };

   const onMarkerClick = async (comment: IComment) => {
      updateSelectedConversationState({SelectedComment: comment});

      if (comment.annotation) {
         updateAnnotationManagerState({annotationMode: IsQuizzing ? AnnotationModes.none : AnnotationModes.display});
      }

      const mediaInterface = await importMediaInterface();
      mediaInterface.pause();
      mediaInterface.currentTime = comment.time;
   };

   const getPreviewText = (comment: IComment): string => {
      if (!comment.text && !!comment.annotation) {
         return t('conversations.annotationAttached');
      } else if (comment.isDeleted) {
         return t('conversations.commentDeletedText');
      }
      return comment.text;
   };

   return (
      <CommentMarkerPlugin>
         {!!mediaDuration && comments && comments.length > 0 && comments.map((comment, index) => (
            <CommentMarkerWrapper
               key={`marker-wrapper-${comment.time}-${index}`}
               isHovering={previewState.showPreview && previewState.comment.id === comment.id}
               isSelected={SelectedComment && SelectedComment.time === comment.time}
               theme={theme}
               leftOffset={(Math.max(Math.min(comment.time / mediaDuration * 100, 100), 0))}
               onMouseEnter={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => setPreviewState({authorName: comment.user.DisplayName, text: getPreviewText(comment), showPreview: true, previewXoffset: calculatePreviewX(e.target as Element), comment: comment})}
               onMouseLeave={() => setPreviewState({authorName: '', text: '', showPreview: false, previewXoffset: -1, comment: null})}
            >
               <ControlButton
                  key={`marker-${comment.time}`}
                  glyph={<CommentMarker />}
                  buttonType={TscButtonType.floating}
                  onClick={() => onMarkerClick(comment)}
                  title=""
                  ariaLabel={t('conversations.commentMarkAriaLabel', {time: TimeFormatter.formatTimeForSpeech(comment.time), user: comment.user.DisplayName, commentText: comment.text})}
                  onBlur={() => setPreviewState({authorName: '', text: '', showPreview: false, previewXoffset: -1, comment: null})}
                  width="0.875rem"
                  themeName={theme}
                  testId={`comment-marker-${index}`}
               />
            </CommentMarkerWrapper>
         ))}
         {previewState.showPreview && (
            <CommentPreview leftOffset={previewState.previewXoffset}>
               <PreviewAuthor>
                  {previewState.authorName}
               </PreviewAuthor>
               <PreviewText>
                  {previewState.text}
               </PreviewText>
            </CommentPreview>
         )}
      </CommentMarkerPlugin>
   );
};

export interface ICommentMarkersProps {
   mediaDuration: number;
}

export interface IStateMappedProps {
   SelectedComment?: IComment;
   comments: IComment[];
   IsQuizzing: boolean;
}

const mapStatesToProps = (
   {mediaDuration}: ICommentMarkersProps,
   {SelectedComment}: Partial<IStateMappedProps>,
   {comments}: Partial<IStateMappedProps>,
   {IsQuizzing}: Partial<IStateMappedProps>
): ICommentMarkersProps & IStateMappedProps => ({mediaDuration, SelectedComment, comments, IsQuizzing});

export default withMemoizedContexts(mapStatesToProps, selectedConversationStore, commentsStore, quizDataStore)(CommentMarkersBase);
